import { faCog, faGlobe, faClockRotateLeft } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { getMessagePayload, config } from "../utils"
import type { WorkflowItem } from "../routes/microservices-processing"
import { Link, useFetcher } from "react-router-dom"
import { CopyButton } from "./CopyButton"

export const ProcessingItem = ({ message }: ProcessingItemProps) => {
  const fetcher = useFetcher()
  const metadata = (fetcher.data as Record<string, string> | undefined) ?? null
  const { hasFileReference, docsUrl, metadataUrl } = getMessagePayload(message.payload)

  function fetchMetadata() {
    if (metadataUrl.length !== 0) {
      fetcher.load("/process-metadata?metadataUrl=" + metadataUrl)
    }
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-3.5 rounded-lg border-[1px] border-accent bg-base-100 p-4">
      <h3 className="rounded-md border border-error p-2 text-sm font-semibold text-error-content dark:border-base-content dark:text-base-content text-center w-full">
        {(message.payload.clientName ?? message.payload.ClientName)}: {message.workflowId}
      </h3>
      <div className="grid w-full grow gap-3 text-base-content grid-rows-[repeat(2,max-content)]">
        <div className="grid col-start-1 ml-6 gap-3">
          <div className="col-start-1 col-end-3 grid min-w-0 gap-2.5">
            {hasFileReference && (
              <>
                <a
                  href={`${config.viewerUrl}${docsUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  title="View document"
                  className="grid grid-cols-[max-content,minmax(0,1fr)] items-center justify-start gap-3 text-left"
                >
                  <FontAwesomeIcon icon={faGlobe} />
                  <span className="col-start-2">
                    Weblink
                  </span>
                </a>
                <button type="button" onClick={fetchMetadata}>
                  <label
                    htmlFor={`metadata-modal-${metadataUrl}`}
                    title="Open metadata modal"
                    className={
                      hasFileReference
                        ? "grid grid-cols-[max-content,minmax(0,1fr)] items-center justify-start gap-3 text-left cursor-pointer"
                        : "hidden"
                    }
                  >
                    <FontAwesomeIcon icon={faCog} />
                    Metadata
                  </label>
                </button>
              </>
            )}
            <Link
              to={`/microservices/history?workflowId=${message.workflowId}`}
              title="View workflow history"
              className="grid grid-cols-[max-content,minmax(0,1fr)] items-center justify-start gap-3 text-left"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
              <span className="col-start-2">History</span>
            </Link>

            <p className="sm:mr-3">
              Id: <strong>{message.workflowId}</strong>
            </p>
            <p className="sm:mr-3">
              Step: <strong>{message.step}</strong>
            </p>
            <p className="sm:mr-3">
              Started: <strong>{message.timeStamp}</strong>
            </p>
          </div>
          <div
            id={`payload-${message.workflowId}`}
            aria-label="Message's Payload"
            className="col-start-3 col-end-6 grid grid-flow-col grid-rows-[repeat(auto-fill,minmax(20px,1fr))] gap-x-4 gap-y-2.5"
          >
            {Object.entries(message.payload).map(([key, value]) => (
              <p key={key} className="text-black flex min-w-0 items-center justify-start gap-2">
                <span className="text-sm">{key}:</span>
                <span className="truncate font-bold">{typeof value === "object" ? JSON.stringify(value) : value}</span>
              </p>
            ))}
          </div>
        </div>
      </div>
      <input type="checkbox" id={`metadata-modal-${metadataUrl}`} className="modal-toggle" hidden />
      <label htmlFor={`metadata-modal-${metadataUrl}`} className="modal cursor-pointer">
        <label htmlFor="" className="modal-box w-11/12 max-w-5xl">
          {metadata !== null ? (
            <>
              <header className="flex items-center justify-between pb-5">
                <h2 className="text-lg font-bold text-center">Metadata</h2>
                <CopyButton
                  id={`copy-process-metadata-${metadataUrl}`}
                  label="Copy Metadata"
                  copiedLabel="Metadata Copied!"
                />
              </header>
              <table className="overflow-x-auto scrollbar-none table w-full">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody id={`copy-process-metadata-${metadataUrl}`}>
                  {Object.entries(metadata).map(([key, value]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td className="whitespace-normal break-words">{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <label htmlFor={`metadata-modal-${metadataUrl}`} className="modal-action">
                <span className="btn btn-sm">Close</span>
              </label>
            </>
          ) : (
            <span className="justify-center align-center flex text-xl py-5">Loading...</span>
          )}
        </label>
      </label>
    </div>
  )
}

interface ProcessingItemProps {
  message: WorkflowItem
}
